/**
 * Numbers the Pro wizard shows next to the form: how precise a sample is, how
 * long the materials run, roughly how long a respondent spends in the study.
 * All of it is arithmetic on the draft — nothing here is a promise the backend
 * makes, and the copy that shows it says "ok." / "szacunkowo".
 */
import { CALIBRATION_VARIANT_SHAPES } from '@/lib/calibrationVariants'
import { askableQuestions } from './askable'
import type { StimulusDraft, StudyDraft } from './types'

/**
 * Margin of error of a proportion at 95% confidence, in percentage points, for
 * the worst case p = 0.5. It is what "how many people is enough" means for the
 * questionnaire answers; it says nothing about the emotion signal, and the
 * wizard words it as precision of the answers, not of the study.
 */
export function marginOfErrorPp(n: number): number | null {
  if (!Number.isFinite(n) || n < 1) return null
  return 1.96 * Math.sqrt(0.25 / n) * 100
}

/** How long one material is on screen, when the draft knows it. */
export function stimulusSeconds(s: StimulusDraft): number | null {
  if (s.durationSeconds != null) return s.durationSeconds
  if ((s.type === 'image' || s.type === 'text') && s.exposureMs) return s.exposureMs / 1000
  return null
}

export function totalStimulusSeconds(stimuli: StimulusDraft[]): number {
  return stimuli.reduce((sum, s) => sum + (stimulusSeconds(s) ?? 0), 0)
}

/** 65 → "1:05", 3725 → "1:02:05". */
export function formatDuration(seconds: number): string {
  const total = Math.round(seconds)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${ss}` : `${m}:${ss}`
}

// Rough per-item costs of the respondent journey, in seconds. The consent and
// camera screens take about a minute; an answer with text takes longer than a
// tap on a scale.
const ONBOARDING_S = 60
const ANSWER_S = { text: 25, choice: 10 } as const
// A Figma task runs until the goal or the limit; half the limit is a fair guess.
const FIGMA_SHARE_OF_LIMIT = 0.5

/** Whole minutes, rounded up, or null when there is nothing to time yet. */
export function respondentMinutes(draft: StudyDraft): number | null {
  if (draft.stimuli.length === 0) return null
  let seconds = ONBOARDING_S
  for (const s of draft.stimuli) {
    const known = stimulusSeconds(s)
    if (known != null) seconds += known
    else if (s.type === 'figma' && s.exposureMs) seconds += (s.exposureMs / 1000) * FIGMA_SHARE_OF_LIMIT
  }
  if (draft.eyeTracking) seconds += CALIBRATION_VARIANT_SHAPES[draft.calibrationVariant].approxSeconds
  for (const q of askableQuestions(draft.questions, draft.demographics)) {
    seconds += q.kind === 'text' ? ANSWER_S.text : ANSWER_S.choice
  }
  return Math.max(1, Math.ceil(seconds / 60))
}
